<script setup>
// Export PDF for a ladder, a league cycle (table or grid), a tournament or an
// americano cycle (club admins and super admins only). The shell, the ticks
// and their gate are the shared ExportPdfDialog, the same one the meet
// printout uses (owner decision, 7 Oct 2026).
//
// The preview is PAGE IMAGES of the real PDF (GET {id}/pdf/preview, built by
// the API from the same document as the file), never the PDF itself: a PDF
// shown in the browser carries the browser's own Download and Print, which
// would go round the permission tick (owner finding, 7 Oct 2026). The PDF
// (GET {id}/pdf/file, streamed, never stored) is fetched only when Print or
// Download is pressed with the tick given. Changing the under 18 tick, or the
// league layout, asks for the preview again, so what is previewed is what
// goes out.
//
// The under 18 tick is offered only when the API says a name is shortened
// (tournament and americano rows carry no age for a club admin, so only the
// API knows), as on the meet printout.
import {computed, ref, watch} from 'vue';
import ExportPdfDialog from '~/components/competitions/ExportPdfDialog.vue';
import {useFileDownload} from '~/composables/useFileDownload';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {printPdfBlob, runExportPdfAction} from '~/composables/useExportPdf';
import {
  competitionPdfDescription,
  competitionPdfEndpoint,
  competitionPdfErrorMessage,
  competitionPdfFileName,
  competitionPdfPreviewEndpoint,
  namesShortenedFromPreview,
  previewMoreLine,
  previewPages,
  previewPageSrc,
  EXPORT_PDF_DOWNLOAD,
  EXPORT_PDF_PRINT,
  EXPORT_PDF_TITLE,
  LEAGUE_PDF_TYPES,
} from '~/composables/useCompetitionPdf';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  // 'ladder' | 'leagueCycle' | 'tournament' | 'americanoCycle'
  format: {type: String, required: true},
  competitionId: {type: String, required: true},
  // For the file name when the browser cannot read the server's.
  name: {type: String, default: ''},
});
const emit = defineEmits(['update:modelValue']);

const {fetchFile, saveFile} = useFileDownload();
const {httpRequest} = useHttpRequest();

// The second tick: under 18s in full. The shared dialog clears it on open.
const juniorFullNames = ref(false);
// League cycles only: the table or the grid.
const type = ref('table');

const preview = ref(null);
const loading = ref(false);
const error = ref(null);
const busy = ref(null);
// Only the latest request may land: a slow first answer must not replace the
// preview asked for after the tick changed.
let requestNo = 0;

const options = () => ({type: type.value, juniorFullNames: juniorFullNames.value});
const pages = computed(() => previewPages(preview.value));
const ready = computed(() => pages.value.length > 0 && !loading.value);
const namesShortened = computed(() => namesShortenedFromPreview(preview.value));
const moreLine = computed(() => previewMoreLine(preview.value));
const description = computed(() => competitionPdfDescription(props.format, options()));

const loadPreview = async () => {
  const mine = ++requestNo;
  loading.value = true;
  error.value = null;
  try {
    const body = await httpRequest(competitionPdfPreviewEndpoint(props.format, props.competitionId, options()));
    if (mine !== requestNo || !props.modelValue) return;
    preview.value = body;
  } catch (e) {
    if (mine !== requestNo) return;
    preview.value = null;
    error.value = competitionPdfErrorMessage(e);
  } finally {
    if (mine === requestNo) loading.value = false;
  }
};

watch(() => props.modelValue, (open) => {
  // Cleared on close as well as open, so reopening asks for one preview, not two.
  requestNo++;
  juniorFullNames.value = false;
  type.value = 'table';
  preview.value = null;
  error.value = null;
  busy.value = null;
  if (open) loadPreview();
}, {immediate: true});

watch([juniorFullNames, type], () => {
  if (props.modelValue) loadPreview();
});

// The ONLY place the PDF is fetched: Print and Download, after the tick.
const fetchPdf = async () => fetchFile(competitionPdfEndpoint(props.format, props.competitionId, options()));

const act = async (action, ticked, deliver) => {
  busy.value = action;
  error.value = null;
  try {
    await runExportPdfAction({ticked, ready: ready.value, fetchPdf, deliver});
  } catch (e) {
    let body = null;
    try {
      body = typeof e?.json === 'function' ? await e.json() : null;
    } catch {
      body = null;
    }
    error.value = competitionPdfErrorMessage(body);
  } finally {
    busy.value = null;
  }
};

const doPrint = ({ticked} = {}) => act('print', ticked, ({blob}) => printPdfBlob(blob));

const doDownload = ({ticked} = {}) => act('download', ticked, ({blob, fileName}) =>
    saveFile(blob, fileName ?? competitionPdfFileName(props.format, {name: props.name, type: type.value})));
</script>

<template>
  <export-pdf-dialog
      :model-value="modelValue"
      :title="EXPORT_PDF_TITLE"
      :max-width="760"
      :description="description"
      :ready="ready"
      :offer-junior-tick="namesShortened"
      v-model:juniorFullNames="juniorFullNames"
      downloadable
      :print-label="EXPORT_PDF_PRINT"
      :download-label="EXPORT_PDF_DOWNLOAD"
      :busy="busy"
      :error="error && pages.length > 0 ? error : null"
      preview-label="Preview of the PDF"
      testid-prefix="competition-pdf"
      @update:model-value="emit('update:modelValue', $event)"
      @print="doPrint"
      @download="doDownload"
  >
    <template #before>
      <div v-if="format === 'leagueCycle'" class="tl-seg mb-4" role="tablist">
        <button
            v-for="t in LEAGUE_PDF_TYPES" :key="t.value"
            class="tl-seg__opt" :class="{ 'tl-seg__opt--active': type === t.value }"
            role="tab" :aria-selected="type === t.value" :data-testid="'competition-pdf-type-' + t.value"
            @click="type = t.value">
          {{ t.label }}
        </button>
      </div>
    </template>

    <template #preview>
      <v-alert v-if="error && pages.length === 0" type="error" variant="tonal" density="compact">{{ error }}</v-alert>
      <div v-else-if="pages.length === 0" class="cp-dialog__wait">
        <v-progress-circular indeterminate color="primary"/>
      </div>
      <div v-else class="cp-dialog__pages" :class="{ 'cp-dialog__pages--stale': loading }" data-testid="competition-pdf-preview">
        <img v-for="(page, i) in pages" :key="i" :src="previewPageSrc(page)" :alt="`Page ${i + 1} of the PDF`"
             draggable="false" class="cp-dialog__page">
        <p v-if="moreLine" class="cp-dialog__more">{{ moreLine }}</p>
      </div>
    </template>
  </export-pdf-dialog>
</template>

<style scoped lang="scss">
.cp-dialog__wait {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40vh;
}

.cp-dialog__pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* Each page as an image, white like paper. Not draggable or selectable, so
   the preview is something to look at, not something to save. */
.cp-dialog__page {
  display: block;
  width: 100%;
  max-width: 640px;
  height: auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

/* A new preview is on its way (the under 18 tick or the layout changed). */
.cp-dialog__pages--stale {
  opacity: 0.4;
}

.cp-dialog__more {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin: 0;
}
</style>
